<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IconTime } from '@/components/design/icons';

import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaStepFlow, {
  type GaiaStepFlowItem,
} from '@/components/design/composite/GaiaStepFlow.vue';
import GaiaWizardFooter from '@/components/design/composite/GaiaWizardFooter.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaSummaryTile from '@/components/design/composite/GaiaSummaryTile.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import WizardLayout from '@/components/design/layout/WizardLayout.vue';
import {
  gaiaContentWidth,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaShellPadding,
  gaiaTracking,
} from '@/components/design/themes/tokens';

import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import {
  ContractManagementViewPreviewRequest,
  ContractManagementViewPreviewColumn,
  ContractManagementViewStaticColumnsBase,
  ContractManagementViewPreviewTableResponse,
  ContractManagementViewCreateFromPreview,
  AndCriterionInput,
  AccessVisibility,
  EnumResponseType,
} from '@/api';

import GaiaVaultPicker from '@/components/design/domain/fileVault/GaiaVaultPicker.vue';
import ContractViewGrid, { type ColumnDef } from '@/components/ContractViewGrid.vue';
import CreateColumnDrawer from '@/components/contractManagement/CreateColumnDrawer.vue';
import UpdateColumnDrawer from '@/components/contractManagement/UpdateColumnDrawer.vue';
import EditStaticColumnDrawer from '@/components/contractManagement/EditStaticColumnDrawer.vue';
import type {
  PreviewColumnData,
  PreviewStaticColumnData,
} from '@/types/contractManagement/columnPreview';
import type { TreeNode } from 'primevue/treenode';
import {
  type StaticColumnType,
  type ConfigurableStaticColumnType,
  STATIC_COLUMN_CONFIGS,
  isEntityBasedStaticColumn,
} from '@/types/contractManagement/viewTypes';
import { logger } from '@/utils/logger';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import { columnQuestion } from '@/utils/contractManagementView/columnQuestion';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useContractTypeStore } from '@/stores/contractTypeStore';

/**
 * Unified column display type for the table
 * Represents both static (default) and custom columns
 */
interface DisplayColumn {
  id: string;
  name: string;
  type: 'static' | 'custom';
  staticType?: StaticColumnType; // For static columns: 'contractual_party', 'matter', 'owner', 'effective_from', 'effective_to', …
  visible: boolean;
  order_index: number;
  hasFilters: boolean;
  prompt?: string; // For custom columns
  previewData?: PreviewColumnData; // Reference to original data for custom columns
}

const router = useRouter();
const message = useGaiaToast();
const contractTypeStore = useContractTypeStore();

// --- Step State ---
const currentStep = ref<number>(1);
const totalSteps = 4;

// --- Form State ---
const saving = ref<boolean>(false);
/** Surfaced under the name field once Continue / Create has been refused. */
const nameError = ref<string | null>(null);

// Step 1: Metadata
const formData = ref<{ name: string; description: string | null; visibility: AccessVisibility }>({
  name: '',
  description: null,
  visibility: AccessVisibility.PRIVATE,
});

// Step 2: Scope — folders (each with its whole subtree) and single contracts
const sourceDirectoryUids = ref<string[]>([]);
const sourceContractUids = ref<string[]>([]);

// Step 3: Columns
const previewColumns = ref<PreviewColumnData[]>([]);
const showCreateColumnDrawer = ref<boolean>(false);
const showUpdateColumnDrawer = ref<boolean>(false);
const showStaticColumnDrawer = ref<boolean>(false);

const editingColumn = ref<PreviewColumnData | null>(null);
/** The prompts the custom columns link, so the prompt picker can mark them. */
const usedPromptUids = computed(() =>
  previewColumns.value.flatMap(col => (col.linkedPrompt ? [col.linkedPrompt.uid] : []))
);
const editingStaticColumnType = ref<StaticColumnType | null>(null);
/** Custom column whose inline "Delete …?" strip is open. Default columns can't be removed. */
const confirmColumnId = ref<string | null>(null);
const updateColumnInitialTab = ref<string>('settings');
const staticColumnInitialTab = ref<string>('settings');

// Static column filters (for preview mode - these don't persist until view is created)
const staticColumnFilters = ref<{
  contractual_party: AndCriterionInput | null;
  matter: AndCriterionInput | null;
  owner: AndCriterionInput | null;
  effective_from: AndCriterionInput | null;
  effective_to: AndCriterionInput | null;
  created_at: AndCriterionInput | null;
  type: AndCriterionInput | null;
  tags: AndCriterionInput | null;
  /** The contract-state filter; the amendment-state one is `amendmentStateFilters`. */
  state: AndCriterionInput | null;
  name: AndCriterionInput | null;
}>({
  contractual_party: null,
  matter: null,
  owner: null,
  effective_from: null,
  effective_to: null,
  created_at: null,
  type: null,
  tags: null,
  state: null,
  name: null,
});
const amendmentStateFilters = ref<AndCriterionInput | null>(null);

// Static columns configuration
const staticColumnsConfig = ref<ContractManagementViewStaticColumnsBase>({
  contractual_party_column_name: 'Contractual Parties',
  contractual_party_column_visible: true,
  contractual_party_column_order_index: 0,
  state_column_name: 'State',
  state_column_visible: true,
  state_column_order_index: 1,
  matter_column_name: 'Matter',
  matter_column_visible: true,
  matter_column_order_index: 3,
  created_at_column_name: 'Created At',
  created_at_column_visible: true,
  created_at_column_order_index: 4,
  document_type_column_name: 'Type',
  document_type_column_visible: true,
  document_type_column_order_index: 5,
  contract_tags_column_name: 'Tags',
  contract_tags_column_visible: true,
  contract_tags_column_order_index: 6,
  owner_column_name: 'Owner',
  owner_column_visible: true,
  owner_column_order_index: 7,
  effective_from_column_name: 'Effective from',
  effective_from_column_visible: true,
  effective_from_column_order_index: 8,
  effective_to_column_name: 'Effective to',
  effective_to_column_visible: true,
  effective_to_column_order_index: 9,
});

// --- Preview State ---
const previewData = ref<ContractManagementViewPreviewTableResponse | null>(null);
const isLoadingPreview = ref<boolean>(false);
const previewError = ref<string | null>(null);
const previewCurrentPage = ref<number>(1);
const previewPageSize = ref<number>(10);

// Tree node data for ContractViewGrid
const treeNodeData = ref<TreeNode[]>([]);
const columnDefs = ref<ColumnDef[]>([]);

// Entity name cache for static column filter options (built from preview data)
const entityNameCache = ref<{
  contractual_party: Map<string, string>;
  matter: Map<string, string>;
  owner: Map<string, string>;
  effective_from: Map<string, string>;
  effective_to: Map<string, string>;
}>({
  contractual_party: new Map(),
  matter: new Map(),
  owner: new Map(),
  effective_from: new Map(),
  effective_to: new Map(),
});

// Build enum response type for contract type from preview filter options, falling back
// to the organisation's contract types
const buildEnumResponseType = (
  values: (string | null)[] | undefined,
  multipleChoices = false
): EnumResponseType | undefined => {
  if (!values) return undefined;

  const normalized = values.map(v => (v ?? '').trim()).filter(v => v.length > 0);

  if (normalized.length === 0) return undefined;

  const unique = Array.from(new Set(normalized));

  return {
    multiple_choices: multipleChoices,
    values: unique.map(name => ({
      name,
      definition: '',
    })),
  };
};

// Build enum response type for contract type from preview filter options, with enum fallback
const typeEnumResponseType = computed((): EnumResponseType | undefined => {
  const filterOptions = previewData.value?.filter_options['type'] as (string | null)[] | undefined;
  const fromFilters = buildEnumResponseType(filterOptions);
  if (fromFilters) return fromFilters;

  return {
    multiple_choices: false,
    values: contractTypeStore.sortedContractTypes.map(type => ({
      name: type.name,
      definition: '',
      color: null,
    })),
  };
});

const tagsEnumResponseType = computed(() => {
  const filterOptions = previewData.value?.filter_options['tags'] as (string | null)[] | undefined;
  return buildEnumResponseType(filterOptions, true);
});

// --- Computed Properties ---
const canProceedToStep2 = computed(() => {
  return formData.value.name.trim().length > 0;
});

// Only the name gates anything: picking no folders or contracts is a valid
// scope (the whole vault), and columns are optional.
const canCreateView = computed(() => canProceedToStep2.value);

// Computed: Check if Continue button should be disabled based on current step
const isContinueDisabled = computed(() => currentStep.value === 1 && !canProceedToStep2.value);

// Computed: Check if a step is clickable (can navigate to it)
const isStepClickable = computed(() => {
  return (stepNumber: number): boolean => {
    // Can always go back to previous steps
    if (stepNumber < currentStep.value) {
      return true;
    }
    // Can go to next step only if current step requirements are met
    if (stepNumber === currentStep.value + 1) {
      return currentStep.value !== 1 || canProceedToStep2.value;
    }
    // Cannot go to future steps beyond next
    return false;
  };
});

const previewCount = computed(() => previewData.value?.result_count ?? 0);

/**
 * The step flow. `locked` mirrors `isStepClickable` so a step ahead of the
 * next reachable one stays inert rather than silently doing nothing.
 */
const railItems = computed<GaiaStepFlowItem[]>(() =>
  // Short labels on purpose — step 4's own heading is "Review & permissions",
  // but spelling that out in the small-screen bar crowds the strip.
  ['View details', 'Select contracts', 'Configure columns', 'Permissions'].map((label, index) => {
    const stepNumber = index + 1;
    return {
      key: String(stepNumber),
      label,
      number: stepNumber,
      done: stepNumber < currentStep.value,
      locked: !(stepNumber === currentStep.value || isStepClickable.value(stepNumber)),
    };
  })
);

// --- Step 4 review ---------------------------------------------------------
/** "2 folders · 3 contracts" — empty when nothing is picked. */
const scopeParts = computed(() =>
  (
    [
      [sourceDirectoryUids.value.length, 'folder'],
      [sourceContractUids.value.length, 'contract'],
    ] as const
  )
    .filter(([count]) => count > 0)
    .map(([count, noun]) => `${count} ${noun}${count === 1 ? '' : 's'}`)
);

const scopeCountLabel = computed(() =>
  scopeParts.value.length > 0
    ? `Selected: ${scopeParts.value.join(' · ')}`
    : 'Nothing selected — the view covers every contract in the vault.'
);

/** The scope in a few words, for the review step. */
const scopeSummary = computed(() =>
  scopeParts.value.length > 0 ? scopeParts.value.join(' · ') : 'Whole vault'
);

const columnCountLabel = computed(() => {
  const custom = previewColumns.value.length;
  return `${allDisplayColumns.value.length} columns · ${custom} custom`;
});

// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
const summaryDescription = computed(() => formData.value.description?.trim() || 'Not set');

const isVisibleToOrg = computed(() => formData.value.visibility === AccessVisibility.VISIBLE);

const visibilityHint = computed(() =>
  isVisibleToOrg.value
    ? 'Everyone in the organisation can open this view.'
    : 'Only you can open this view.'
);

function setVisibility(visible: boolean): void {
  formData.value.visibility = visible ? AccessVisibility.VISIBLE : AccessVisibility.PRIVATE;
}

/** "2 folders · 12 columns" — what the create call is about to start work on. */
const summaryLine = computed(() => {
  const columns = allDisplayColumns.value.length;
  return `${scopeSummary.value} · ${columns} column${columns === 1 ? '' : 's'}`;
});

// Combined columns for display (static + custom, sorted by order_index)
const allDisplayColumns = computed((): DisplayColumn[] => {
  const columns: DisplayColumn[] = [];

  // Name column is always visible, not renameable, not reorderable.
  // It only supports filtering, so it appears here with no visibility toggle.
  columns.push({
    id: 'name',
    name: 'Name',
    type: 'static',
    staticType: 'name',
    visible: true,
    order_index: -1,
    hasFilters: Boolean(staticColumnFilters.value.name?.criteria.length),
  });

  // Add static columns using STATIC_COLUMN_CONFIGS to ensure all are included
  // 'name' is intentionally excluded from this iteration: it has no name/visible/order_index backend fields
  const staticColumnTypes: ConfigurableStaticColumnType[] = [
    'contractual_party',
    'state',
    'matter',
    'created_at',
    'type',
    'tags',
    'owner',
    'effective_from',
    'effective_to',
  ];

  for (const staticType of staticColumnTypes) {
    const config = STATIC_COLUMN_CONFIGS[staticType];
    const nameKey = config.nameKey as keyof ContractManagementViewStaticColumnsBase;
    const visibleKey = config.visibleKey as keyof ContractManagementViewStaticColumnsBase;
    const orderIndexKey = config.orderIndexKey as keyof ContractManagementViewStaticColumnsBase;

    const name = staticColumnsConfig.value[nameKey] as string | undefined;
    const visible = staticColumnsConfig.value[visibleKey] as boolean | undefined;
    const orderIndex = staticColumnsConfig.value[orderIndexKey] as number | undefined;

    // Get filter key for this static column type
    const filterKey = staticType as keyof typeof staticColumnFilters.value;
    const hasFilters =
      Boolean(staticColumnFilters.value[filterKey]?.criteria.length) ||
      (staticType === 'state' && Boolean(amendmentStateFilters.value?.criteria.length));

    columns.push({
      id: config.fieldKey,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      name: name || config.fieldKey,
      type: 'static',
      staticType,
      visible: visible ?? true,
      order_index: orderIndex ?? 0,
      hasFilters,
    });
  }

  // Add custom columns
  for (const col of previewColumns.value) {
    columns.push({
      id: col.id,
      name: col.name,
      type: 'custom',
      visible: col.visible,
      order_index: col.order_index + 10, // Offset by 10 for static columns (0-9)
      hasFilters: Boolean(col.filters?.criteria.length),
      prompt: col.question?.prompt,
      previewData: col,
    });
  }

  // Sort by order_index
  return columns.sort((a, b) => a.order_index - b.order_index);
});

// --- Preview Fetching ---
async function fetchPreview(): Promise<void> {
  isLoadingPreview.value = true;
  previewError.value = null;

  try {
    const offset = (previewCurrentPage.value - 1) * previewPageSize.value;

    // Build preview columns for the API (including filters if present)
    // Only include columns with questions (question is required)
    const apiColumns: ContractManagementViewPreviewColumn[] = previewColumns.value
      .filter(
        (
          col
        ): col is PreviewColumnData & { question: NonNullable<PreviewColumnData['question']> } =>
          col.question !== null
      )
      .map(col => ({
        name: col.name,
        visible: col.visible,
        order_index: col.order_index + 10, // Offset by 10 for static columns (0-9)
        question: columnQuestion(
          col.question,
          col.linkedPrompt,
          col.contractTypeUids,
          col.promptName
        ),
        // Include filters if they have criteria
        ...(col.filters?.criteria.length && { filters: col.filters }),
      }));

    // Build static columns config with filters
    const staticColumnsWithFilters: ContractManagementViewStaticColumnsBase = {
      ...staticColumnsConfig.value,
      // Include filters if they have criteria
      ...(staticColumnFilters.value.contractual_party?.criteria.length && {
        contractual_party_filters: staticColumnFilters.value.contractual_party,
      }),
      ...(staticColumnFilters.value.matter?.criteria.length && {
        matter_filters: staticColumnFilters.value.matter,
      }),
      ...(staticColumnFilters.value.owner?.criteria.length && {
        owner_filters: staticColumnFilters.value.owner,
      }),
      ...(staticColumnFilters.value.effective_from?.criteria.length && {
        effective_from_filters: staticColumnFilters.value.effective_from,
      }),
      ...(staticColumnFilters.value.effective_to?.criteria.length && {
        effective_to_filters: staticColumnFilters.value.effective_to,
      }),
      ...(staticColumnFilters.value.created_at?.criteria.length && {
        created_at_filters: staticColumnFilters.value.created_at,
      }),
      ...(staticColumnFilters.value.type?.criteria.length && {
        document_type_filters: staticColumnFilters.value.type,
      }),
      ...(staticColumnFilters.value.tags?.criteria.length && {
        contract_tags_filters: staticColumnFilters.value.tags,
      }),
      ...(staticColumnFilters.value.state?.criteria.length && {
        contract_state_filters: staticColumnFilters.value.state,
      }),
      ...(amendmentStateFilters.value?.criteria.length && {
        amendment_state_filters: amendmentStateFilters.value,
      }),
      ...(staticColumnFilters.value.name?.criteria.length && {
        name_filters: staticColumnFilters.value.name,
      }),
    };

    const request: ContractManagementViewPreviewRequest = {
      source_directory_uids: sourceDirectoryUids.value,
      source_contract_uids: sourceContractUids.value,
      columns: apiColumns,
      static_columns: staticColumnsWithFilters,
      limit: previewPageSize.value,
      offset,
      sort_key: 'created_at',
      sort_direction: 'desc',
    };

    const response = await ContractViewsService.getPreviewTable(request);
    previewData.value = response;

    // Transform data for the grid
    transformPreviewDataToGrid(response);
  } catch (error) {
    logger.error('Error fetching preview:', error);
    previewError.value = error instanceof Error ? error.message : 'Failed to load preview';
    previewData.value = null;
    treeNodeData.value = [];
  } finally {
    isLoadingPreview.value = false;
  }
}

// Transform API response to TreeNode format for ContractViewGrid
function transformPreviewDataToGrid(response: ContractManagementViewPreviewTableResponse): void {
  const { results, columns } = response;

  // Build column definitions
  const baseColumns: ColumnDef[] = [
    {
      headerName: '#',
      field: '__rowNumber',
      width: 60,
      minWidth: 60,
      frozen: true,
      alignFrozen: 'left',
      isRowNumber: true,
    },
  ];

  const nameColumn: ColumnDef = {
    field: 'name',
    headerName: 'Name',
    width: 300,
    minWidth: 250,
    expander: true,
    isStaticColumn: true,
    filters: staticColumnFilters.value.name,
  };

  // IMPORTANT: The API returns extractions ONLY for VISIBLE columns, sorted by order_index
  // So we must filter to visible columns first, then sort by order_index
  // This ensures the extraction array indices match the column indices
  const visibleColumns = columns.filter(col => col.visible);
  const sortedVisibleColumns = [...visibleColumns].sort((a, b) => a.order_index - b.order_index);

  const dataColumnDefs: ColumnDef[] = sortedVisibleColumns.map(col => {
    const isStaticColumn = col.kind !== 'dynamic';
    // Use STATIC_COLUMN_CONFIGS to get the correct fieldKey for static columns
    let fieldKey: string;
    if (isStaticColumn) {
      const staticType = col.kind as ConfigurableStaticColumnType;
      fieldKey = STATIC_COLUMN_CONFIGS[staticType].fieldKey || `__static_${col.kind}`;
    } else {
      fieldKey = `__dynamic_${col.name}`;
    }

    return {
      field: fieldKey,
      headerName: col.name,
      width: 200,
      minWidth: 150,
      isStaticColumn,
      responseType: col.question?.response_type,
    };
  });

  columnDefs.value = [...baseColumns, nameColumn, ...dataColumnDefs];

  // Transform results to tree nodes
  const treeData: TreeNode[] = results.map(([contract, extractions], index) => {
    const contractData: Record<string, unknown> = {
      __rowNumber: (previewCurrentPage.value - 1) * previewPageSize.value + index + 1,
      name: contract.name,
      uid: contract.uid,
      type: contract.contract_type?.name ?? '',
      description: contract.description,
      tags: contract.tags,
      created_at: contract.created_at,
      updated_at: contract.updated_at,
    };

    // Map extractions to VISIBLE columns only (extractions array corresponds to visible columns)
    for (const [colIndex, col] of sortedVisibleColumns.entries()) {
      if (colIndex >= extractions.length) continue;

      const extractionValue = extractions[colIndex];
      const isStaticColumn = col.kind !== 'dynamic';
      // Use STATIC_COLUMN_CONFIGS to get the correct fieldKey for static columns
      let fieldKey: string;
      if (isStaticColumn) {
        const staticType = col.kind as ConfigurableStaticColumnType;
        fieldKey = STATIC_COLUMN_CONFIGS[staticType].fieldKey || `__static_${col.kind}`;
      } else {
        fieldKey = `__dynamic_${col.name}`;
      }

      if (isStaticColumn) {
        const staticType = col.kind as StaticColumnType;

        // Handle entity-based static columns (contractual_party, matter, owner, effective_from, effective_to)
        if (isEntityBasedStaticColumn(staticType)) {
          if (staticType === 'owner') {
            if (
              extractionValue &&
              typeof extractionValue === 'object' &&
              'uid' in extractionValue
            ) {
              const owner = extractionValue as {
                uid: string;
                full_name?: string | null;
                email?: string | null;
              };
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must normalise to null
              const displayName = owner.full_name || owner.email || null;
              contractData[fieldKey] = displayName;
              contractData[`${fieldKey}_data`] = extractionValue;
              if (owner.uid && displayName) {
                entityNameCache.value.owner.set(owner.uid, displayName);
              }
            } else {
              contractData[fieldKey] = null;
            }
          } else if (extractionValue !== null && Array.isArray(extractionValue)) {
            const entities = extractionValue as { uid: string }[];
            const names = entities.map(e => staticEntityName(e)).filter(n => n.length > 0);
            contractData[fieldKey] = names.length > 0 ? names.join(', ') : null;
            contractData[`${fieldKey}_data`] = extractionValue;

            // Populate entity name cache for filter options
            if (staticType in entityNameCache.value) {
              for (const entity of entities) {
                const name = staticEntityName(entity);
                if (entity.uid && name) {
                  entityNameCache.value[staticType].set(entity.uid, name);
                }
              }
            }
          } else {
            contractData[fieldKey] = null;
          }
        } else {
          // Handle primitive static columns (created_at, type, tags)
          switch (staticType) {
            case 'created_at': {
              // created_at is a string (ISO date)
              const createdAt =
                typeof extractionValue === 'string' ? extractionValue : contract.created_at;
              contractData[fieldKey] = createdAt;

              break;
            }
            case 'type': {
              // type is a string
              const documentType =
                typeof extractionValue === 'string'
                  ? extractionValue
                  : contract.contract_type?.name;
              contractData[fieldKey] = documentType;

              break;
            }
            case 'tags': {
              // tags is an array of strings
              const tags = Array.isArray(extractionValue) ? extractionValue : contract.tags;
              contractData[fieldKey] = tags;

              break;
            }
            default: {
              contractData[fieldKey] = extractionValue ?? null;
            }
          }
        }
      } else {
        // Dynamic column - extraction or null (will be null in preview since no actual extraction exists)
        if (extractionValue && typeof extractionValue === 'object' && 'value' in extractionValue) {
          const extraction = extractionValue;
          contractData[fieldKey] =
            extraction.human_overwrite?.value === undefined
              ? extraction.value
              : extraction.human_overwrite.value;
        } else {
          contractData[fieldKey] = null;
        }
      }
    }

    return {
      key: contract.uid,
      data: contractData,
      leaf: true, // No children in preview mode
    };
  });

  treeNodeData.value = treeData;
}

// Watch for changes that should trigger preview refresh
watch(
  [sourceDirectoryUids, sourceContractUids, previewColumns, staticColumnsConfig],
  () => {
    if (currentStep.value >= 2) {
      previewCurrentPage.value = 1;
      void fetchPreview();
    }
  },
  { deep: true }
);

// --- Step Navigation ---
function goToStep(step: number): void {
  if (step < 1 || step > totalSteps) return;

  // Allow going back to any previous step
  if (step < currentStep.value) {
    currentStep.value = step;
    // Fetch preview when entering step 2 or 3
    if (step >= 2) {
      void nextTick(() => fetchPreview());
    }
    return;
  }

  // Validate before moving forward
  if (step > currentStep.value && currentStep.value === 1 && !canProceedToStep2.value) {
    nameError.value = 'Please enter a view name';
    message.warning('Please enter a view name');
    return;
  }

  currentStep.value = step;

  // Fetch preview when entering step 2 or 3
  if (step >= 2) {
    void nextTick(() => fetchPreview());
  }
}

// Handle step click from step indicator
function handleStepClick(stepNumber: number): void {
  // Can only go back or to next step if requirements are met
  if (stepNumber < currentStep.value) {
    // Always allow going back
    goToStep(stepNumber);
  } else if (stepNumber === currentStep.value + 1) {
    // Allow going to next step if requirements are met
    goToStep(stepNumber);
  }
  // Otherwise, do nothing (cannot skip steps or go to future steps)
}

function nextStep(): void {
  goToStep(currentStep.value + 1);
}

function prevStep(): void {
  goToStep(currentStep.value - 1);
}

// --- Column Management ---
function openAddColumnDrawer(): void {
  showCreateColumnDrawer.value = true;
}

function openEditColumnDrawer(column: PreviewColumnData): void {
  editingColumn.value = column;
  updateColumnInitialTab.value = 'settings';
  showUpdateColumnDrawer.value = true;
}

function openFilterColumnDrawer(column: PreviewColumnData): void {
  editingColumn.value = column;
  updateColumnInitialTab.value = 'filters';
  showUpdateColumnDrawer.value = true;
}

// Static column drawer handlers
function openStaticColumnEditDrawer(staticType: StaticColumnType): void {
  editingStaticColumnType.value = staticType;
  staticColumnInitialTab.value = 'settings';
  showStaticColumnDrawer.value = true;
}

function openStaticColumnFilterDrawer(staticType: StaticColumnType): void {
  editingStaticColumnType.value = staticType;
  staticColumnInitialTab.value = 'filters';
  showStaticColumnDrawer.value = true;
}

function handleColumnCreated(column: PreviewColumnData): void {
  // Add new columns at the end of the list
  column.order_index = previewColumns.value.length;
  previewColumns.value.push(column);
}

function handleColumnUpdated(column: PreviewColumnData): void {
  const index = previewColumns.value.findIndex(c => c.id === column.id);
  if (index !== -1) {
    previewColumns.value[index] = column;
  }
}

function handleColumnDeleted(columnId: string): void {
  previewColumns.value = previewColumns.value.filter(c => c.id !== columnId);
  // Reindex order
  for (const [index, col] of previewColumns.value.entries()) {
    col.order_index = index;
  }
}

function removeColumn(columnId: string): void {
  confirmColumnId.value = null;
  previewColumns.value = previewColumns.value.filter(c => c.id !== columnId);
  // Reindex order
  for (const [index, col] of previewColumns.value.entries()) {
    col.order_index = index;
  }
}

// Computed property for the current editing static column
const editingStaticColumnData = computed((): PreviewStaticColumnData | null => {
  if (!editingStaticColumnType.value) return null;

  const type = editingStaticColumnType.value;
  const configMap: Record<
    StaticColumnType,
    { name: string; visible: boolean; order_index: number }
  > = {
    contractual_party: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.contractual_party_column_name || 'Contractual Parties',
      visible: staticColumnsConfig.value.contractual_party_column_visible ?? true,
      order_index: staticColumnsConfig.value.contractual_party_column_order_index ?? 0,
    },
    matter: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.matter_column_name || 'Matter',
      visible: staticColumnsConfig.value.matter_column_visible ?? true,
      order_index: staticColumnsConfig.value.matter_column_order_index ?? 3,
    },
    created_at: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.created_at_column_name || 'Created At',
      visible: staticColumnsConfig.value.created_at_column_visible ?? true,
      order_index: staticColumnsConfig.value.created_at_column_order_index ?? 4,
    },
    type: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.document_type_column_name || 'Type',
      visible: staticColumnsConfig.value.document_type_column_visible ?? true,
      order_index: staticColumnsConfig.value.document_type_column_order_index ?? 5,
    },
    tags: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.contract_tags_column_name || 'Tags',
      visible: staticColumnsConfig.value.contract_tags_column_visible ?? true,
      order_index: staticColumnsConfig.value.contract_tags_column_order_index ?? 6,
    },
    owner: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.owner_column_name || 'Owner',
      visible: staticColumnsConfig.value.owner_column_visible ?? true,
      order_index: staticColumnsConfig.value.owner_column_order_index ?? 7,
    },
    effective_from: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.effective_from_column_name || 'Effective from',
      visible: staticColumnsConfig.value.effective_from_column_visible ?? true,
      order_index: staticColumnsConfig.value.effective_from_column_order_index ?? 8,
    },
    effective_to: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.effective_to_column_name || 'Effective to',
      visible: staticColumnsConfig.value.effective_to_column_visible ?? true,
      order_index: staticColumnsConfig.value.effective_to_column_order_index ?? 9,
    },
    state: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      name: staticColumnsConfig.value.state_column_name || 'State',
      visible: staticColumnsConfig.value.state_column_visible ?? true,
      order_index: staticColumnsConfig.value.state_column_order_index ?? 1,
    },
    name: {
      name: 'Name',
      visible: true,
      order_index: 0,
    },
  };

  const config = configMap[type];
  return {
    type,
    name: config.name,
    visible: config.visible,
    order_index: config.order_index,
    filters: staticColumnFilters.value[type],
    ...(type === 'state' ? { amendmentFilters: amendmentStateFilters.value } : {}),
  };
});

// Handle static column updates (for preview mode)
function handleStaticColumnUpdated(column: PreviewStaticColumnData): void {
  const type = column.type;

  // Name column has no STATIC_COLUMN_CONFIGS entry; only filters are tracked.
  if (type !== 'name') {
    const config = STATIC_COLUMN_CONFIGS[type];
    const visibleKey = config.visibleKey as keyof ContractManagementViewStaticColumnsBase;
    staticColumnsConfig.value[visibleKey] = column.visible as never;
  }

  // Update filters
  staticColumnFilters.value[type] = column.filters;
  if (type === 'state') amendmentStateFilters.value = column.amendmentFilters ?? null;

  // Trigger preview refresh
  void fetchPreview();
}

// --- Save / Cancel ---
async function handleSave(): Promise<void> {
  if (!canCreateView.value) {
    nameError.value = 'Please enter a view name';
    message.error('Please enter a view name');
    goToStep(1);
    return;
  }

  saving.value = true;

  try {
    // Build columns array from preview columns (custom columns only)
    // Only include columns with questions (question is required)
    // IMPORTANT: Add +3 offset to order_index to place custom columns after static columns (0, 1, 2)
    const columns: ContractManagementViewPreviewColumn[] = previewColumns.value
      .filter(
        (
          col
        ): col is PreviewColumnData & { question: NonNullable<PreviewColumnData['question']> } =>
          col.question !== null
      )
      .map(col => ({
        name: col.name,
        visible: col.visible,
        order_index: col.order_index + 10, // Offset by 10 past the static columns (contractual_party=0, matter=3, created_at=4, type=5, tags=6, owner=7, effective_from=8, effective_to=9)
        filters: col.filters ?? undefined,
        question: columnQuestion(
          {
            prompt: col.question.prompt,
            response_type: col.question.response_type,
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
            example_data: col.question.example_data || undefined,
          },
          col.linkedPrompt,
          col.contractTypeUids,
          col.promptName
        ),
      }));

    // Build the complete request body
    // Static column properties are spread directly into the request (not nested in static_columns)
    const createRequest: ContractManagementViewCreateFromPreview = {
      name: formData.value.name.trim(),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      description: formData.value.description?.trim() || undefined,
      visibility: formData.value.visibility,
      source_directory_uids: sourceDirectoryUids.value,
      source_contract_uids: sourceContractUids.value,
      columns: columns.length > 0 ? columns : undefined,
      // Static column properties are directly on the request object
      contractual_party_column_name: staticColumnsConfig.value.contractual_party_column_name,
      contractual_party_column_visible: staticColumnsConfig.value.contractual_party_column_visible,
      contractual_party_column_order_index:
        staticColumnsConfig.value.contractual_party_column_order_index,
      contractual_party_filters: staticColumnFilters.value.contractual_party ?? undefined,
      state_column_name: staticColumnsConfig.value.state_column_name,
      state_column_visible: staticColumnsConfig.value.state_column_visible,
      state_column_order_index: staticColumnsConfig.value.state_column_order_index,
      contract_state_filters: staticColumnFilters.value.state ?? undefined,
      amendment_state_filters: amendmentStateFilters.value ?? undefined,
      matter_column_name: staticColumnsConfig.value.matter_column_name,
      matter_column_visible: staticColumnsConfig.value.matter_column_visible,
      matter_column_order_index: staticColumnsConfig.value.matter_column_order_index,
      matter_filters: staticColumnFilters.value.matter ?? undefined,
      created_at_column_name: staticColumnsConfig.value.created_at_column_name,
      created_at_column_visible: staticColumnsConfig.value.created_at_column_visible,
      created_at_column_order_index: staticColumnsConfig.value.created_at_column_order_index,
      created_at_filters: staticColumnFilters.value.created_at ?? undefined,
      document_type_column_name: staticColumnsConfig.value.document_type_column_name,
      document_type_column_visible: staticColumnsConfig.value.document_type_column_visible,
      document_type_column_order_index: staticColumnsConfig.value.document_type_column_order_index,
      document_type_filters: staticColumnFilters.value.type ?? undefined,
      contract_tags_column_name: staticColumnsConfig.value.contract_tags_column_name,
      contract_tags_column_visible: staticColumnsConfig.value.contract_tags_column_visible,
      contract_tags_column_order_index: staticColumnsConfig.value.contract_tags_column_order_index,
      contract_tags_filters: staticColumnFilters.value.tags ?? undefined,
      owner_column_name: staticColumnsConfig.value.owner_column_name,
      owner_column_visible: staticColumnsConfig.value.owner_column_visible,
      owner_column_order_index: staticColumnsConfig.value.owner_column_order_index,
      owner_filters: staticColumnFilters.value.owner ?? undefined,
      effective_from_column_name: staticColumnsConfig.value.effective_from_column_name,
      effective_from_column_visible: staticColumnsConfig.value.effective_from_column_visible,
      effective_from_column_order_index:
        staticColumnsConfig.value.effective_from_column_order_index,
      effective_from_filters: staticColumnFilters.value.effective_from ?? undefined,
      effective_to_column_name: staticColumnsConfig.value.effective_to_column_name,
      effective_to_column_visible: staticColumnsConfig.value.effective_to_column_visible,
      effective_to_column_order_index: staticColumnsConfig.value.effective_to_column_order_index,
      effective_to_filters: staticColumnFilters.value.effective_to ?? undefined,
      name_filters: staticColumnFilters.value.name ?? undefined,
    };

    // Create view from preview in a single API call
    const createdView = await ContractViewsService.createFromPreview(createRequest);

    message.success('View created successfully');

    await router.push({
      name: 'contract-management-view-detail',
      params: { viewId: createdView.uid },
    });
  } catch (error) {
    logger.error('Error creating view:', error);
    message.error(error instanceof Error ? error.message : 'Failed to create view');
  } finally {
    saving.value = false;
  }
}

// --- Grid Event Handlers (Preview mode - mostly no-ops) ---
function handleNodeExpand(): void {
  // No expansion in preview mode
}

function handleEditCell(): void {
  // No editing in preview mode
}

function handleGoToContract(): void {
  // No navigation in preview mode
}

function handleGoToContractFile(): void {
  // No navigation in preview mode
}

function handleEditColumnFilters(data: { field: string; isStaticColumn: boolean }): void {
  // In preview mode only the Name column exposes a filter icon directly on the header
  if (data.field === 'name') {
    openStaticColumnFilterDrawer('name');
  }
}

/**
 * One row's two entry points into the drawers. The row doesn't know whether a
 * column is static or custom — it just says "settings" or "filters" and this
 * routes to the drawer that owns that kind.
 */
function openColumnSettings(column: DisplayColumn): void {
  if (column.type === 'static' && column.staticType) {
    openStaticColumnEditDrawer(column.staticType);
  } else if (column.previewData) {
    openEditColumnDrawer(column.previewData);
  }
}

function openColumnFilters(column: DisplayColumn): void {
  if (column.type === 'static' && column.staticType) {
    openStaticColumnFilterDrawer(column.staticType);
  } else if (column.previewData) {
    openFilterColumnDrawer(column.previewData);
  }
}

// --- Chrome ----------------------------------------------------------------
const pageX = gaiaShellPadding.pageX;
const recordWidth = gaiaContentWidth.record;
const cardBorder = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
const innerBorder = gaiaNeutral.borderCard;
const headSurface = gaiaNeutral.surfaceSoft;
const headBorder = gaiaNeutral.borderDivider;
const headColor = gaiaNeutral.textSubtle;
const rowBorder = gaiaNeutral.borderSoft;
const headIcon = gaiaNeutral.textSubtle;
const pendingIcon = gaiaNeutral.textMuted;
// The inner tables' header row is CSS, not a GaiaText, so its type arrives by bind.
const headSize = gaiaFontSize.nano;
const headWeight = String(gaiaFontWeight.bold);
const headTracking = gaiaTracking.label;

// --- Lifecycle ---
onMounted(() => {
  void contractTypeStore.ensureLoaded();
});
</script>

<template>
  <wizard-layout content-padding="0" content-max-width="100%">
    <!-- Crumbs carry the way back out of the wizard; the mock has no Cancel. -->
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.contractManagement }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'contract-management-views' }">{{ navLabels.views }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>New view</n-breadcrumb-item>
    </template>

    <template #rail>
      <n-flex vertical :size="14" :wrap="false" class="gaia-wizard-rail">
        <n-flex vertical :size="4" :wrap="false">
          <gaia-header :level="1" size="page">New view</gaia-header>
          <gaia-text
            class="gaia-wizard-rail__hint"
            size="meta"
            tone="subtle"
            :style="{ lineHeight: 1.5 }"
          >
            Name it, pick the contracts, shape the columns — nothing is written until Create.
          </gaia-text>
        </n-flex>
        <gaia-step-flow
          :items="railItems"
          :active="String(currentStep)"
          @select="(key: string) => handleStepClick(Number(key))"
        />
      </n-flex>
    </template>

    <div class="view-create">
      <div class="body">
        <div class="content">
          <!-- Step card. Every step shares the one surface; the preview below
             stays mounted throughout so it doesn't re-fetch on each step. -->
          <div class="card step-card">
            <!-- Step 1 — View details -->
            <n-flex v-if="currentStep === 1" vertical :size="16" data-tour="views.step-details">
              <div>
                <gaia-text size="subtitle" weight="semibold" tone="ink">View details</gaia-text>
                <gaia-text
                  size="control"
                  tone="subtle"
                  :style="{ display: 'block', marginTop: '3px' }"
                >
                  Name the view so colleagues can tell what it answers.
                </gaia-text>
              </div>
              <n-flex :size="16" align="flex-start">
                <div :style="{ flex: '0 0 320px' }">
                  <gaia-form-field label="View name" required :error="nameError || undefined">
                    <gaia-input
                      :value="formData.name"
                      size="medium"
                      placeholder="e.g. Renewal radar 2026"
                      data-tour="views.name-input"
                      :status="nameError ? 'error' : undefined"
                      style="width: 100%"
                      @update:value="
                        (value: string) => {
                          formData.name = value;
                          nameError = null;
                        }
                      "
                    />
                  </gaia-form-field>
                </div>
                <div :style="{ flex: '1 1 340px', minWidth: 0 }">
                  <gaia-form-field label="Description" hint="Optional">
                    <gaia-input
                      :value="formData.description ?? ''"
                      size="medium"
                      placeholder="What this view tracks, and for whom"
                      style="width: 100%"
                      @update:value="(value: string) => (formData.description = value || null)"
                    />
                  </gaia-form-field>
                </div>
              </n-flex>
            </n-flex>

            <!-- Step 2 — Select contracts -->
            <n-flex
              v-else-if="currentStep === 2"
              vertical
              :size="14"
              data-tour="views.step-contracts"
            >
              <div style="min-width: 0">
                <gaia-text size="subtitle" weight="semibold" tone="ink">Select contracts</gaia-text>
                <gaia-text
                  size="control"
                  tone="subtle"
                  :style="{ display: 'block', marginTop: '3px' }"
                >
                  A folder includes every contract in it and its subfolders, including ones added
                  later. A contract is included on its own. Pick nothing to cover every contract in
                  the vault.
                </gaia-text>
              </div>
              <gaia-vault-picker
                v-model:directory-uids="sourceDirectoryUids"
                v-model:contract-uids="sourceContractUids"
                data-tour="views.vault-picker"
              />
              <gaia-text size="meta" tone="subtle">{{ scopeCountLabel }}</gaia-text>
            </n-flex>

            <!-- Step 3 — Configure columns -->
            <n-flex
              v-else-if="currentStep === 3"
              vertical
              :size="14"
              data-tour="views.step-columns"
            >
              <n-flex align="flex-start" :size="12" :wrap="false">
                <div style="min-width: 0">
                  <gaia-text size="subtitle" weight="semibold" tone="ink">
                    Configure columns
                  </gaia-text>
                  <gaia-text
                    size="control"
                    tone="subtle"
                    :style="{ display: 'block', marginTop: '3px' }"
                  >
                    {{ columnCountLabel }} · custom columns are AI questions asked of every contract
                  </gaia-text>
                </div>
                <gaia-add-button
                  subject="column"
                  variant="primary"
                  :style="{ marginLeft: 'auto' }"
                  data-tour="views.add-column"
                  @click="openAddColumnDrawer"
                />
              </n-flex>

              <div class="inner-table">
                <div class="inner-head column-grid">
                  <div>Column</div>
                  <div>Question</div>
                  <div />
                </div>
                <div class="column-scroll">
                  <template v-for="column in allDisplayColumns" :key="column.id">
                    <gaia-table-row-state
                      v-if="confirmColumnId === column.id"
                      variant="confirm"
                      :title="`Delete “${column.name}”?`"
                      @cancel="confirmColumnId = null"
                      @confirm="removeColumn(column.id)"
                    >
                      Its saved question and any filters are removed.
                    </gaia-table-row-state>

                    <!-- The mock puts a drag handle before the name. Reordering
                       isn't wired up in this wizard, so the track is dropped
                       rather than left empty — an empty 24px track just pushed
                       the column names in from the card edge. Order comes from
                       the column's order_index. -->
                    <div v-else class="inner-row column-grid">
                      <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
                        <gaia-text truncate size="body" weight="semibold" tone="ink">
                          {{ column.name }}
                        </gaia-text>
                        <gaia-tag v-if="column.type === 'static'" variant="label">Default</gaia-tag>
                        <!-- Name is the row's identity: it can't be hidden or
                           reordered, which the lock says without a tooltip. -->
                        <svg
                          v-if="column.staticType === 'name'"
                          width="11"
                          height="11"
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.4"
                          :style="{ flex: 'none', color: pendingIcon }"
                        >
                          <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
                          <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" />
                        </svg>
                        <!-- Hidden columns still list, but say so: the grid won't
                           show them until they're switched back on. The chip is
                           also the shortest way back — it opens the same drawer
                           the pencil does, on the tab holding the toggle. -->
                        <gaia-tag
                          v-if="!column.visible"
                          variant="amber"
                          title="Hidden — open column settings to show it"
                          :style="{ cursor: 'pointer' }"
                          @click="openColumnSettings(column)"
                        >
                          Hidden
                        </gaia-tag>
                      </n-flex>

                      <gaia-text
                        truncate
                        size="dense"
                        :tone="column.type === 'static' ? 'blank' : 'tertiary'"
                        :style="{ fontStyle: column.type === 'static' ? 'italic' : 'normal' }"
                        :title="column.prompt"
                      >
                        {{ column.type === 'static' ? '—' : column.prompt || '—' }}
                      </gaia-text>

                      <n-flex justify="end" :size="2" :wrap="false">
                        <gaia-icon-button
                          :dimension="26"
                          quaternary
                          title="Edit column"
                          @click="openColumnSettings(column)"
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path
                              d="M13.5 8.5v4a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h4"
                            />
                            <path d="M12.7 2.3 7 8v1h1l5.7-5.7a.7.7 0 0 0-1-1z" />
                          </svg>
                        </gaia-icon-button>
                        <gaia-icon-button
                          :dimension="26"
                          :quaternary="!column.hasFilters"
                          :secondary="column.hasFilters"
                          :title="column.hasFilters ? 'Column filters — active' : 'Column filters'"
                          @click="openColumnFilters(column)"
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M2 3h12l-4.5 5v4l-3 1.5v-5.5z" />
                          </svg>
                        </gaia-icon-button>
                        <gaia-delete-button
                          v-if="column.type === 'custom'"
                          subject="column"
                          @click="confirmColumnId = column.id"
                        />
                      </n-flex>
                    </div>
                  </template>
                </div>
              </div>
            </n-flex>

            <!-- Step 4 — Review & permissions -->
            <n-flex v-else vertical :size="16" data-tour="views.step-visibility">
              <div>
                <gaia-text size="subtitle" weight="semibold" tone="ink">
                  Review &amp; permissions
                </gaia-text>
                <gaia-text
                  size="control"
                  tone="subtle"
                  :style="{ display: 'block', marginTop: '3px' }"
                >
                  Check the setup, decide who can open the view, then create it.
                </gaia-text>
              </div>

              <div class="summary-grid">
                <gaia-summary-tile label="Name" :value="formData.name" />
                <gaia-summary-tile
                  label="Description"
                  :value="summaryDescription"
                  :empty="!formData.description?.trim()"
                />
                <gaia-summary-tile
                  label="Contracts"
                  :value="scopeSummary"
                  clickable
                  @click="goToStep(2)"
                />
                <gaia-summary-tile
                  label="Columns"
                  :value="String(allDisplayColumns.length)"
                  clickable
                  @click="goToStep(3)"
                />
              </div>

              <gaia-toggle-row
                card
                label="Visible to others in the organisation"
                :hint="visibilityHint"
                :checked="isVisibleToOrg"
                @update:checked="setVisibility"
              />

              <gaia-banner plain type="info">
                Create is a single call — AI extraction then starts for {{ summaryLine }}, and the
                grid fills in as results settle.
              </gaia-banner>
            </n-flex>
          </div>

          <!-- Preview card. The grid inside is the app's real one, so expanding,
             cell states and header filters keep working. -->
          <div class="card preview-card" data-tour="views.preview">
            <div class="preview-head">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
                :style="{ flex: 'none', color: headIcon }"
              >
                <path d="M1.5 8s2.4-4 6.5-4 6.5 4 6.5 4-2.4 4-6.5 4-6.5-4-6.5-4z" />
                <circle cx="8" cy="8" r="1.8" />
              </svg>
              <gaia-text variant="label" size="micro" tone="subtle">Preview</gaia-text>
              <gaia-tag v-if="previewCount > 0">
                {{ previewCount }} {{ previewCount === 1 ? 'contract' : 'contracts' }}
              </gaia-tag>
            </div>

            <gaia-table-skeleton
              v-if="isLoadingPreview"
              :row-style="{ gridTemplateColumns: 'minmax(160px, 1fr) 88px' }"
              :cells="['64%', '56px']"
              :rows="5"
              :row-height="44"
              :action="false"
            />

            <gaia-empty-state
              v-else-if="previewError"
              status="alert"
              title="Couldn’t load the preview"
            >
              {{ previewError }}
              <template #actions>
                <gaia-button type="primary" size="small" @click="fetchPreview">Retry</gaia-button>
              </template>
            </gaia-empty-state>

            <gaia-empty-state v-else-if="!previewData" title="The preview starts at step 2">
              <template #icon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M2.5 3.5h13v11h-13z" />
                  <path d="M2.5 7h13M7 7v7.5" />
                </svg>
              </template>
              Continue to pick the contracts — the preview shows the real contracts your view will
              contain.
            </gaia-empty-state>

            <gaia-empty-state v-else-if="treeNodeData.length === 0" title="No contracts found">
              <template #icon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M2.5 3.5h13v11h-13z" />
                  <path d="M2.5 7h13M7 7v7.5" />
                </svg>
              </template>
              No contracts are in the picked scope yet.
            </gaia-empty-state>

            <template v-else>
              <div class="preview-grid">
                <ContractViewGrid
                  :row-data="treeNodeData"
                  :column-defs="columnDefs"
                  :preview-mode="true"
                  @node-expand="handleNodeExpand"
                  @edit-cell="handleEditCell"
                  @go-to-contract="handleGoToContract"
                  @go-to-contract-file="handleGoToContractFile"
                  @edit-column-filters="handleEditColumnFilters"
                >
                  <template #empty-cell>
                    <n-tooltip trigger="hover" placement="top">
                      <template #trigger>
                        <div class="cell-pending-preview">
                          <n-icon :size="18" :color="pendingIcon"><IconTime /></n-icon>
                        </div>
                      </template>
                      Data will be extracted after view creation
                    </n-tooltip>
                  </template>
                </ContractViewGrid>
              </div>

              <div class="preview-foot">
                <n-flex align="center" :size="6" :wrap="false">
                  <n-icon :size="12" :color="pendingIcon"><IconTime /></n-icon>
                  <gaia-text size="meta" tone="subtle">
                    Custom columns fill after the view is created
                  </gaia-text>
                </n-flex>
                <gaia-pagination
                  v-if="previewCount > previewPageSize"
                  v-model:page="previewCurrentPage"
                  v-model:page-size="previewPageSize"
                  :total="previewCount"
                  :page-sizes="[5, 10, 25, 50]"
                  :style="{ marginLeft: 'auto' }"
                  @update:page="fetchPreview"
                  @update:page-size="
                    () => {
                      previewCurrentPage = 1;
                      fetchPreview();
                    }
                  "
                />
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Modals & drawers — the existing ones; only the page around them changed. -->
      <CreateColumnDrawer
        v-model:show="showCreateColumnDrawer"
        preview-mode
        :current-column-count="previewColumns.length"
        :used-prompt-uids="usedPromptUids"
        @preview-created="handleColumnCreated"
      />

      <UpdateColumnDrawer
        v-model:show="showUpdateColumnDrawer"
        preview-mode
        :preview-column="editingColumn"
        :used-prompt-uids="usedPromptUids"
        :initial-tab="updateColumnInitialTab"
        @preview-updated="handleColumnUpdated"
        @preview-deleted="handleColumnDeleted"
      />

      <EditStaticColumnDrawer
        v-if="editingStaticColumnType"
        v-model:show="showStaticColumnDrawer"
        preview-mode
        :static-column-type="editingStaticColumnType"
        :view-data="null"
        :preview-static-column="editingStaticColumnData"
        :entity-name-cache="entityNameCache"
        :type-enum-response-type="typeEnumResponseType"
        :tags-enum-response-type="tagsEnumResponseType"
        :initial-tab="staticColumnInitialTab"
        @preview-updated="handleStaticColumnUpdated"
      />
    </div>

    <template #footer>
      <!-- `loading` already says the save is in flight; the label doesn't swap
           to "Creating…" to say it twice. -->
      <gaia-wizard-footer
        :back-label="currentStep > 1 ? 'Back' : undefined"
        :next-label="currentStep < totalSteps ? 'Continue' : 'Create view'"
        :loading="saving"
        :next-disabled="currentStep < totalSteps ? isContinueDisabled : !canCreateView"
        :back-attrs="{ 'data-tour': 'views.wizard-back' }"
        :next-attrs="{ 'data-tour': 'views.wizard-continue' }"
        @back="prevStep"
        @next="currentStep < totalSteps ? nextStep() : handleSave()"
      />
    </template>
  </wizard-layout>
</template>

<style scoped>
/* WizardLayout's body scrolls the page; the root grows with the steps. */
.view-create {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}

.body {
  flex: 1;
  min-height: 0;
  padding: 24px v-bind(pageX) 56px;
}

/* The mock caps at 1290px, but it was drawn at a 1440px viewport where that
   cap never bites — the cards simply fill the main area. Taking the number
   literally strands the wizard in the left third of a wide screen, so the cap
   matches the entity lists instead and the proportions hold. */
.content {
  max-width: v-bind(recordWidth);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card {
  border: 1px solid v-bind(cardBorder);
  border-radius: 12px;
  background: v-bind(surface);
}

.step-card {
  padding: 22px;
}

/* The compact tables inside a step: their own hairline box, not the page's
   data card, so they read as part of the step rather than a list of records. */
.inner-table {
  border: 1px solid v-bind(innerBorder);
  border-radius: 10px;
  overflow: hidden;
}

.inner-head {
  height: 34px;
  padding: 0 14px;
  background: v-bind(headSurface);
  border-bottom: 1px solid v-bind(headBorder);
  font-size: v-bind(headSize);
  font-weight: v-bind(headWeight);
  letter-spacing: v-bind(headTracking);
  text-transform: uppercase;
  color: v-bind(headColor);
}

.inner-row {
  height: 44px;
  padding: 0 14px;
  border-bottom: 1px solid v-bind(rowBorder);
}

.inner-row:last-child {
  border-bottom: none;
}

.column-grid {
  display: grid;
  align-items: center;
  gap: 12px;
  grid-template-columns: minmax(120px, 1fr) minmax(140px, 1.3fr) 96px;
}

/* Nine defaults plus every custom column is a long list; the head stays put
   while the rows scroll. */
.column-scroll {
  max-height: 224px;
  overflow-y: auto;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 12px;
}

.preview-card {
  overflow: hidden;
}

.preview-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: v-bind(headSurface);
  border-bottom: 1px solid v-bind(headBorder);
}

/* Revogrid measures itself against this box, so the height is fixed here
   rather than left to the content. */
.preview-grid {
  height: 420px;
}

.preview-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 6px 14px;
  background: v-bind(headSurface);
  border-top: 1px solid v-bind(headBorder);
}

.cell-pending-preview {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }
}
</style>
